import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Grid,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Paper,
  CircularProgress,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  Search as SearchIcon,
  Clear as ClearIcon,
  Print as PrintIcon,
  LockOutlined as LockOutlinedIcon,
  Description as DescriptionIcon,
  FactCheck as FactCheckIcon,
  AttachMoney as AttachMoneyIcon,
  Visibility as VisibilityIcon,
} from '@mui/icons-material';
import { tienePermisosModulo } from 'lib/seguridad/permisosPorModulo';
import { FP, PageHeader, StatCard, fpInputSx } from 'components/financepro';
import { useProductosMaterialContext } from 'context';
import PrintListado from 'utils/PrintListado';
import VistaNotaCredito from './VistaNotaCredito';
import DetalleEcf, { ChipEstadoEcf } from 'Fiscal/DetalleEcf';

const PROGRAMA_LISTADO_NOTAS = 'notacreditodebitoemitidas';
const PROGRAMAS_FALLBACK = ['mantenimientofacturasemitidas', 'facturacion'];

const ListadoNotasCreditoDebitoEmitidas = () => {
  const { controller, selectedCompany, showSuccess, showError } = useProductosMaterialContext();
  const {
    nombreNegocio,
    usuarioLog,
    claveLog,
    roleDetalles,
    direccion_empresa: direccionEmpresaCtx,
    rnc_empresa: rncEmpresaCtx,
  } = controller;
  const servidor = process.env.REACT_APP_SERVIDOR || '';

  const puedeLeer =
  tienePermisosModulo(roleDetalles, PROGRAMA_LISTADO_NOTAS) 
  
  const [notas, setNotas] = useState([]);
  const [cargando, setCargando] = useState(false);
  const [datosEmpresa, setDatosEmpresa] = useState(null);
  const [fechaDesde, setFechaDesde] = useState('');
  const [fechaHasta, setFechaHasta] = useState('');
  const [tipoFiltro, setTipoFiltro] = useState('todos');
  const [cliente, setCliente] = useState('');
  const [vistaNotaAbierta, setVistaNotaAbierta] = useState(false);
  const [datosVistaNota, setDatosVistaNota] = useState(null);
  const [reimprimiendoKey, setReimprimiendoKey] = useState(null);
  const [detalleEcfAbierto, setDetalleEcfAbierto] = useState(false);
  const [notaDetalleEcf, setNotaDetalleEcf] = useState(null);

  /**
   * Datos de empresa para el encabezado de PrintListado.
   * Prioridad: configuración de factura (nombre, dirección, teléfono, RNC, email)
   * y, si faltan campos, contexto de login (nombreNegocio, direccion_empresa, rnc_empresa).
   */
  const cargarDatosEmpresa = useCallback(async () => {
    if (!puedeLeer || !selectedCompany?.db_empresa) return;
    try {
      const response = await fetch(servidor + '/api/facturabackend_obtenerconfiguracion', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id_empresa: selectedCompany,
          usuario: usuarioLog || '',
          password: claveLog || '',
          tipo_documento: 'Factura',
        }),
      });
      const config = response.ok
        ? (await response.json().catch(() => null))?.configuracion || null
        : null;

      const nombreCtx =
        typeof nombreNegocio === 'string' && nombreNegocio.trim() && nombreNegocio !== 'false'
          ? nombreNegocio.trim()
          : '';
      const direccionCtx =
        typeof direccionEmpresaCtx === 'string' && direccionEmpresaCtx.trim()
          ? direccionEmpresaCtx.trim()
          : '';
      const rncCtx = String(rncEmpresaCtx ?? '').replace(/\D/g, '');

      setDatosEmpresa({
        nombre:
          (config?.nombreEmpresa || '').trim() ||
          selectedCompany?.name ||
          selectedCompany?.empresa ||
          nombreCtx ||
          'Empresa',
        direccion: (config?.direccionEmpresa || '').trim() || direccionCtx,
        telefono: (config?.telefonoEmpresa || '').trim(),
        rnc: (config?.rncEmpresa || '').trim() || rncCtx,
        email: (config?.emailEmpresa || '').trim(),
      });
    } catch {
      const nombreCtx =
        typeof nombreNegocio === 'string' && nombreNegocio.trim() && nombreNegocio !== 'false'
          ? nombreNegocio.trim()
          : '';
      setDatosEmpresa({
        nombre: selectedCompany?.name || selectedCompany?.empresa || nombreCtx || 'Empresa',
        direccion:
          typeof direccionEmpresaCtx === 'string' ? direccionEmpresaCtx.trim() : '',
        telefono: '',
        rnc: String(rncEmpresaCtx ?? '').replace(/\D/g, ''),
        email: '',
      });
    }
  }, [
    puedeLeer,
    selectedCompany,
    usuarioLog,
    claveLog,
    servidor,
    nombreNegocio,
    direccionEmpresaCtx,
    rncEmpresaCtx,
  ]);

  useEffect(() => {
    cargarDatosEmpresa();
  }, [cargarDatosEmpresa]);

  const cargarNotas = useCallback(async () => {
    if (!puedeLeer || !selectedCompany?.db_empresa) return;
    setCargando(true);
    try {
      const response = await fetch(servidor + '/api/facturabackend_listarnotasemitidas', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id_empresa: selectedCompany,
          usuario: usuarioLog || '',
          password: claveLog || '',
          fecha_desde: fechaDesde || undefined,
          fecha_hasta: fechaHasta || undefined,
          tipo: tipoFiltro,
          cliente: cliente.trim() || undefined,
        }),
      });
      if (response.ok) {
        const data = await response.json();
        const lista = data.notas || [];
        setNotas(lista);
        if (lista.length === 0) {
          showSuccess('No se encontraron notas con los filtros aplicados');
        }
      } else {
        const errorData = await response.json().catch(() => ({}));
        showError(errorData.Error || 'Error al cargar notas emitidas');
      }
    } catch {
      showError('Error de conexión al cargar notas emitidas');
    } finally {
      setCargando(false);
    }
  }, [
    puedeLeer,
    selectedCompany,
    usuarioLog,
    claveLog,
    servidor,
    fechaDesde,
    fechaHasta,
    tipoFiltro,
    cliente,
    showError,
    showSuccess,
  ]);

  const handleLimpiarFiltros = () => {
    setFechaDesde('');
    setFechaHasta('');
    setTipoFiltro('todos');
    setCliente('');
  };

  const formatearFecha = (fecha) => {
    if (!fecha) return '—';
    try {
      return new Date(fecha).toLocaleDateString('es-DO', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
      });
    } catch {
      return fecha;
    }
  };

  const etiquetaTipo = (t) => (t === 'ND' ? 'Nota de débito' : 'Nota de crédito');

  const abrirDetalleEcf = (n) => {
    if (!n?.ncf) {
      showError('Esta nota no tiene NCF electrónico para consultar.');
      return;
    }
    setNotaDetalleEcf({
      ...n,
      encf: n.ncf,
      nfact: n.ncred,
      tipofactura: etiquetaTipo(n.tipo),
      nombre_cliente: n.suplidor || n.descripcion || '',
      monto_total: n.total,
      fecha_factura: n.fecha,
      estado: n.ecf_estado,
    });
    setDetalleEcfAbierto(true);
  };

  const onDetalleEcfCerrar = () => {
    setDetalleEcfAbierto(false);
    setNotaDetalleEcf(null);
  };

  const reimprimirNota = async (n) => {
    if (!usuarioLog) {
      showError('Sesión inválida: falta usuario de login');
      return;
    }
    const key = `${n.tipo}-${n.ncred}`;
    setReimprimiendoKey(key);
    try {
      const response = await fetch(servidor + '/api/facturabackend_obtenernotaemitida', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id_empresa: selectedCompany,
          usuario: usuarioLog || '',
          password: claveLog || '',
          tipo: n.tipo,
          ncred: n.ncred,
        }),
      });
      const data = await response.json().catch(() => ({}));
      if (response.ok && data.nota) {
        const rncDigits = String(data.nota.rnc || '').replace(/\D/g, '');
        const rncComprador =
          rncDigits &&
          rncDigits !== '999999' &&
          (rncDigits.length === 9 || rncDigits.length === 11)
            ? rncDigits
            : '';
        setDatosVistaNota({
          ...data.nota,
          id_empresa: selectedCompany,
          rnc: rncComprador,
          cliente_nombre: data.nota?.cliente_nombre,
          rncemisor: datosEmpresa?.rnc || rncEmpresaCtx || '',
          empresa: {
            ...(data.nota.empresa || {}),
            rnc: datosEmpresa?.rnc || rncEmpresaCtx || selectedCompany?.rnc || '',
            nombre: datosEmpresa?.nombre || '',
          },
          monto_total_ecf: data.nota.monto_total_ecf ?? data.nota.total,
        });
        setVistaNotaAbierta(true);
      } else {
        showError(data.Error || 'Error al cargar la nota para reimprimir');
      }
    } catch {
      showError('Error de conexión al cargar la nota');
    } finally {
      setReimprimiendoKey(null);
    }
  };

  const columnasPrint = useMemo(
    () => [
      { id: 'tipo', label: 'Tipo', render: (v) => etiquetaTipo(v) },
      { id: 'ncred', label: 'Nº interno' },
      { id: 'fecha', label: 'Fecha', align: 'center', render: (_, row) => formatearFecha(row.fecha) },
      { id: 'suplidor', label: 'Cliente (código)' },
      { id: 'nfact_origen', label: 'Fact. origen', align: 'center' },
      { id: 'tipofactura_origen', label: 'Tipo fact. origen' },
      {
        id: 'total',
        label: 'Total',
        align: 'right',
        render: (v) =>
          v != null
            ? `$${parseFloat(v).toLocaleString('es-DO', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
            : '—',
      },
      { id: 'ncf', label: 'NCF nota', align: 'center', render: (_, row) => row.ncf || '—' },
      {
        id: 'ecf_estado',
        label: 'Estado ECF',
        align: 'center',
        render: (_, row) => {
          const estado = (row.ecf_estado || '').toLowerCase();
          if (!estado || !row.ncf) return '—';
          return estado.charAt(0).toUpperCase() + estado.slice(1).replace('_', ' ');
        },
      },
      { id: 'motivo', label: 'Motivo / razón', render: (_, row) => row.motivo || '—' },
    ],
    []
  );

  const sumaTotal = notas.reduce((s, n) => s + (parseFloat(n.total) || 0), 0);
  const sumaCreditos = notas
    .filter((n) => n.tipo !== 'ND')
    .reduce((s, n) => s + (parseFloat(n.total) || 0), 0);
  const sumaDebitos = notas
    .filter((n) => n.tipo === 'ND')
    .reduce((s, n) => s + (parseFloat(n.total) || 0), 0);

  const cardShellSx = {
    borderRadius: 2.5,
    border: `1px solid ${FP.color.border}`,
    bgcolor: FP.color.cardBg,
    boxShadow: FP.shadow.soft,
    overflow: 'hidden',
  };

  const filterHeaderSx = {
    px: { xs: 2, md: 3 },
    py: 1.6,
    borderBottom: `1px solid ${FP.color.border}`,
    bgcolor: '#FAFBFE',
    display: 'flex',
    alignItems: 'center',
    gap: 1.2,
  };

  const cellHeadSx = {
    fontWeight: 700,
    fontSize: '0.75rem',
    color: FP.color.textSecondary,
    letterSpacing: '0.04em',
    textTransform: 'uppercase',
    display: 'block',
    mb: 0.5,
  };

  if (!puedeLeer) {
    return (
      <Box component="main" sx={{ minHeight: '100vh', bgcolor: FP.color.surface }}>
        <PageHeader
          title="Notas de crédito y débito emitidas"
          subtitle={
            nombreNegocio
              ? `${nombreNegocio} · Listado consolidado`
              : 'Listado consolidado'
          }
        />
        <Box sx={{ p: { xs: 1.5, sm: 2, md: 3 }, display: 'flex', justifyContent: 'center' }}>
          <Card
            elevation={0}
            sx={{ ...cardShellSx, maxWidth: 560, width: '100%', textAlign: 'center', p: { xs: 3, md: 4 } }}
          >
            <Box
              sx={{
                width: 64,
                height: 64,
                borderRadius: 1.6,
                mx: 'auto',
                mb: 2.5,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: FP.color.dangerSoft,
                color: FP.color.danger,
              }}
            >
              <LockOutlinedIcon sx={{ fontSize: 30 }} />
            </Box>
            <Typography
              sx={{
                fontWeight: 800,
                fontSize: '1.25rem',
                color: FP.color.textPrimary,
                mb: 1,
                letterSpacing: '-0.01em',
              }}
            >
              Acceso denegado
            </Typography>
            <Typography sx={{ color: FP.color.textSecondary, fontSize: '0.95rem', mb: 1.2 }}>
              No tiene permiso del programa «listadonotascreditodebitoemitidas» en roles para ver el listado.
            </Typography>
            <Typography sx={{ color: FP.color.textMuted, fontSize: '0.85rem' }}>
              Por favor, contacte al administrador del sistema para solicitar los permisos necesarios.
            </Typography>
          </Card>
        </Box>
      </Box>
    );
  }

  return (
    <Box component="main" sx={{ minHeight: '100vh', bgcolor: FP.color.surface }}>
      <PageHeader
        title="Notas de crédito y débito emitidas"
        subtitle={nombreNegocio ? `${nombreNegocio} · Listado consolidado` : 'Listado consolidado'}
      />

      <Box sx={{ p: { xs: 1.5, sm: 2, md: 3 } }} id="printable-listado-notas-nc-nd">
        <Card elevation={0} sx={{ ...cardShellSx, mb: 3 }}>
          <Box sx={filterHeaderSx}>
            <Box
              sx={{
                width: 30,
                height: 30,
                borderRadius: 1.2,
                bgcolor: FP.color.accentBlueSoft,
                color: FP.color.accentBlue,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <SearchIcon sx={{ fontSize: 18 }} />
            </Box>
            <Typography sx={{ fontWeight: 700, color: FP.color.textPrimary, fontSize: '0.98rem' }}>
              Filtros de búsqueda
            </Typography>
            <Box sx={{ flex: 1 }} />
            <Box className="no-print">
              <PrintListado
                title={
                  datosEmpresa?.nombre ||
                  selectedCompany?.name ||
                  selectedCompany?.empresa ||
                  'Empresa'
                }
                empresa={datosEmpresa}
                reportTitle="Reporte de notas de crédito / débito emitidas"
                subtitle={
                  [fechaDesde, fechaHasta].filter(Boolean).length
                    ? `Del ${fechaDesde || '...'} al ${fechaHasta || '...'}`
                    : undefined
                }
                columns={columnasPrint}
                data={notas}
                emptyMessage="No hay notas para imprimir"
                printButtonLabel="Imprimir listado"
                printButtonProps={{
                  sx: {
                    textTransform: 'none',
                    fontWeight: 700,
                    borderRadius: 1.4,
                    bgcolor: FP.color.navy,
                    color: '#FFFFFF',
                    boxShadow: 'none',
                    '&:hover': { bgcolor: FP.color.navyDeep, boxShadow: 'none' },
                  },
                }}
                footer={`Total registros: ${notas.length} | Suma importes: $${sumaTotal.toLocaleString(
                  'es-DO',
                  { minimumFractionDigits: 2, maximumFractionDigits: 2 }
                )}`}
              />
            </Box>
          </Box>
          <CardContent sx={{ p: { xs: 2, md: 3 } }} className="no-print">
            <Typography sx={{ color: FP.color.textSecondary, fontSize: '0.92rem', lineHeight: 1.5, mb: 2 }}>
              Filtre por fechas y/o por cliente (código, nombre en descripción, NCF, nº factura origen o nº interno de la nota). Luego pulse «Buscar».
            </Typography>
            <Grid container spacing={2.5} alignItems="flex-end">
              <Grid item xs={12} sm={6} md={2}>
                <TextField
                  fullWidth
                  label="Fecha desde"
                  type="date"
                  value={fechaDesde}
                  onChange={(e) => setFechaDesde(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  sx={fpInputSx}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <TextField
                  fullWidth
                  label="Fecha hasta"
                  type="date"
                  value={fechaHasta}
                  onChange={(e) => setFechaHasta(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  sx={fpInputSx}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <FormControl fullWidth sx={fpInputSx}>
                  <InputLabel id="tipo-filtro-nc-nd" shrink>
                    Tipo
                  </InputLabel>
                  <Select
                    labelId="tipo-filtro-nc-nd"
                    value={tipoFiltro}
                    onChange={(e) => setTipoFiltro(e.target.value)}
                    label="Tipo"
                  >
                    <MenuItem value="todos">Crédito y débito</MenuItem>
                    <MenuItem value="credito">Solo notas de crédito</MenuItem>
                    <MenuItem value="debito">Solo notas de débito</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  label="Cliente / buscar"
                  value={cliente}
                  onChange={(e) => setCliente(e.target.value)}
                  placeholder="Código, texto, NCF, nº factura…"
                  InputLabelProps={{ shrink: true }}
                  sx={fpInputSx}
                />
              </Grid>
              <Grid item xs={12} sm={12} md={3}>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.25 }}>
                  <Button
                    variant="contained"
                    startIcon={<SearchIcon />}
                    onClick={cargarNotas}
                    disabled={cargando}
                    sx={{
                      height: 46,
                      bgcolor: FP.color.navy,
                      color: '#FFFFFF',
                      borderRadius: 1.4,
                      textTransform: 'none',
                      fontWeight: 700,
                      boxShadow: 'none',
                      flex: 1,
                      '&:hover': { bgcolor: FP.color.navyDeep, boxShadow: 'none' },
                    }}
                  >
                    Buscar
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={<ClearIcon />}
                    onClick={handleLimpiarFiltros}
                    sx={{
                      height: 46,
                      borderColor: FP.color.border,
                      color: FP.color.textPrimary,
                      bgcolor: '#FFFFFF',
                      borderRadius: 1.6,
                      textTransform: 'none',
                      fontWeight: 700,
                      flex: 1,
                      '&:hover': { bgcolor: FP.color.surfaceAlt, borderColor: FP.color.borderStrong },
                    }}
                  >
                    Limpiar
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {notas.length > 0 && (
          <Grid container spacing={2} sx={{ mb: 3 }} className="no-print">
            <Grid item xs={12} sm={6} md={4}>
              <StatCard
                label="Notas totales"
                value={String(notas.length)}
                icon={DescriptionIcon}
                accent={FP.color.accentBlue}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <StatCard
                label="Total créditos"
                value={`$${sumaCreditos.toLocaleString('es-DO', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
                icon={FactCheckIcon}
                accent={FP.color.successDark}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <StatCard
                label="Total débitos"
                value={`$${sumaDebitos.toLocaleString('es-DO', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
                icon={AttachMoneyIcon}
                accent={FP.color.warning}
              />
            </Grid>
          </Grid>
        )}

        <Card elevation={0} sx={cardShellSx}>
          <Box
            sx={{
              px: { xs: 2, md: 3 },
              py: 1.6,
              borderBottom: `1px solid ${FP.color.border}`,
              bgcolor: '#FAFBFE',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 1,
              flexWrap: 'wrap',
            }}
          >
            <Typography sx={{ fontWeight: 700, color: FP.color.textPrimary, fontSize: '0.98rem' }}>
              Detalle de notas emitidas
            </Typography>
            {cargando && (
              <Typography sx={{ color: FP.color.textSecondary, fontSize: '0.82rem', fontWeight: 600 }}>
                Cargando…
              </Typography>
            )}
          </Box>
          <CardContent sx={{ p: { xs: 1.5, sm: 2 }, '&:last-child': { pb: { xs: 1.5, sm: 2 } } }}>
            {cargando ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                <CircularProgress size={28} sx={{ color: FP.color.accentBlue }} />
              </Box>
            ) : notas.length === 0 ? (
              <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" py={8}>
                <DescriptionIcon sx={{ fontSize: 56, color: FP.color.textMuted, mb: 2 }} />
                <Typography sx={{ color: FP.color.textPrimary, fontWeight: 700 }} gutterBottom>
                  No hay datos para mostrar
                </Typography>
                <Typography sx={{ color: FP.color.textSecondary, fontSize: '0.9rem' }} textAlign="center" px={2}>
                  Use los filtros y pulse «Buscar» para listar las notas de crédito o débito emitidas.
                </Typography>
              </Box>
            ) : (
              <TableContainer
                component={Paper}
                elevation={0}
                sx={{ border: `1px solid ${FP.color.border}`, borderRadius: 2, overflow: 'auto' }}
              >
                <Table sx={{ tableLayout: 'fixed', width: '100%', minWidth: { xs: 1280, sm: 1420 } }}>
                  <colgroup>
                    {['9%', '7%', '8%', '12%', '7%', '10%', '8%', '10%', '8%', '13%', '8%'].map((w, i) => (
                      <col key={i} style={{ width: w }} />
                    ))}
                  </colgroup>
                  <TableBody>
                    <TableRow
                      sx={{
                        position: 'sticky',
                        top: 0,
                        zIndex: 2,
                        '& td': {
                          border: 'none',
                          borderBottom: `2px solid ${FP.color.border}`,
                          bgcolor: FP.color.surfaceAlt,
                        },
                      }}
                    >
                      <TableCell>
                        <Typography component="span" sx={cellHeadSx}>
                          Tipo
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography component="span" sx={cellHeadSx}>
                          Nº interno
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography component="span" sx={{ ...cellHeadSx, textAlign: 'center', width: '100%' }}>
                          Fecha
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography component="span" sx={cellHeadSx}>
                          Cliente
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography component="span" sx={{ ...cellHeadSx, textAlign: 'center', width: '100%' }}>
                          Fact. origen
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography component="span" sx={cellHeadSx}>
                          Tipo fact. origen
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography component="span" sx={{ ...cellHeadSx, textAlign: 'right', width: '100%' }}>
                          Total
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography component="span" sx={{ ...cellHeadSx, textAlign: 'center', width: '100%' }}>
                          NCF nota
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography component="span" sx={{ ...cellHeadSx, textAlign: 'center', width: '100%' }}>
                          Estado ECF
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography component="span" sx={cellHeadSx}>
                          Motivo
                        </Typography>
                      </TableCell>
                      <TableCell className="no-print">
                        <Typography component="span" sx={{ ...cellHeadSx, textAlign: 'center', width: '100%' }}>
                          Acciones
                        </Typography>
                      </TableCell>
                    </TableRow>
                    {notas.map((n) => (
                      <TableRow key={`${n.tipo}-${n.ncred}`} hover>
                        <TableCell
                          sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
                          title={etiquetaTipo(n.tipo)}
                        >
                          {etiquetaTipo(n.tipo)}
                        </TableCell>
                        <TableCell sx={{ fontWeight: 700, color: FP.color.textPrimary }}>{n.ncred}</TableCell>
                        <TableCell align="center">{formatearFecha(n.fecha)}</TableCell>
                        <TableCell
                          sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
                          title={n.suplidor || '—'}
                        >
                          {n.suplidor || '—'}
                        </TableCell>
                        <TableCell align="center">{n.nfact_origen || '—'}</TableCell>
                        <TableCell
                          sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
                          title={n.tipofactura_origen || '—'}
                        >
                          {n.tipofactura_origen || '—'}
                        </TableCell>
                        <TableCell align="right">
                          ${(parseFloat(n.total) || 0).toLocaleString('es-DO', {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })}
                        </TableCell>
                        <TableCell align="center">{n.ncf || '—'}</TableCell>
                        <TableCell align="center">
                          {!n.ncf ? (
                            <Typography sx={{ color: FP.color.textMuted, fontSize: '0.8rem' }}>—</Typography>
                          ) : (
                            <ChipEstadoEcf estado={n.ecf_estado} />
                          )}
                        </TableCell>
                        <TableCell
                          sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
                          title={n.motivo || '—'}
                        >
                          {n.motivo || '—'}
                        </TableCell>
                        <TableCell align="center" className="no-print">
                          <Box
                            sx={{
                              display: 'flex',
                              gap: 0.4,
                              justifyContent: 'center',
                              alignItems: 'center',
                            }}
                          >
                            <Tooltip title="Reimprimir nota">
                              <span>
                                <IconButton
                                  size="small"
                                  disabled={reimprimiendoKey === `${n.tipo}-${n.ncred}` || !usuarioLog}
                                  onClick={() => reimprimirNota(n)}
                                  sx={{
                                    color: FP.color.accentBlue,
                                    '&:hover': { bgcolor: FP.color.accentBlueSoft },
                                    '&.Mui-disabled': { color: FP.color.textMuted },
                                  }}
                                >
                                  {reimprimiendoKey === `${n.tipo}-${n.ncred}` ? (
                                    <CircularProgress size={18} sx={{ color: FP.color.accentBlue }} />
                                  ) : (
                                    <PrintIcon fontSize="small" />
                                  )}
                                </IconButton>
                              </span>
                            </Tooltip>
                            <Tooltip title={n.ncf ? 'Ver detalle e-CF (aceptación / rechazo)' : 'Sin NCF electrónico'}>
                              <span>
                                <IconButton
                                  size="small"
                                  disabled={!usuarioLog || !n.ncf}
                                  onClick={() => abrirDetalleEcf(n)}
                                  sx={{
                                    color: FP.color.navy,
                                    '&:hover': { bgcolor: FP.color.surfaceAlt },
                                    '&.Mui-disabled': { color: FP.color.textMuted },
                                  }}
                                >
                                  <VisibilityIcon fontSize="small" />
                                </IconButton>
                              </span>
                            </Tooltip>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </CardContent>
        </Card>
      </Box>

      {datosVistaNota && (
        <VistaNotaCredito
          open={vistaNotaAbierta}
          onClose={() => {
            setVistaNotaAbierta(false);
            setDatosVistaNota(null);
          }}
          notaCreditoData={datosVistaNota}
        />
      )}

      <DetalleEcf
        open={detalleEcfAbierto}
        onClose={onDetalleEcfCerrar}
        encf={notaDetalleEcf?.ncf || ''}
        factura={notaDetalleEcf}
        onEstadoActualizado={(actualizado) => {
          const encfActualizado = actualizado?.encf;
          if (!encfActualizado) return;
          setNotas((prev) =>
            prev.map((item) =>
              item.ncf === encfActualizado
                ? { ...item, ecf_estado: actualizado.estado || item.ecf_estado }
                : item
            )
          );
          setNotaDetalleEcf((prev) =>
            prev && prev.ncf === encfActualizado
              ? { ...prev, ecf_estado: actualizado.estado || prev.ecf_estado, estado: actualizado.estado }
              : prev
          );
        }}
      />
    </Box>
  );
};

export default ListadoNotasCreditoDebitoEmitidas;
